import {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {useNullableProcedurePageContext} from 'procedures/ProcedurePageContext';
import APIUtil from 'util/APIUtil';

/** A collection of information describing where or how an Action was invoked
 *
 * Keep in sync with benchling/notebook/structured_tables/api/action_function_context.py
 */
export interface ActionFunctionContext {
  entryId: string | undefined;
  procedureStepExecutionId: string | undefined;
  procedureId: string | undefined;
  procedureMethodDefinitionVersionId: string | undefined;
  requestV2SubmissionId: string | undefined;
}

/** Grab the ActionFunctionContext using any layers in the UI that are available */
export function useActionFunctionContext(): ActionFunctionContext {
  const {fileId} = EditorPropertiesContext.useContext();
  const {deprecatedStepCreationContextId} = WorksheetContext.useContext();
  const documentParentId = DocumentEditorContext.useContext().documentParentId;
  let procedureId: string | undefined;
  let procedureMethodDefinitionVersionId: string | undefined;
  const procedurePageContext = useNullableProcedurePageContext();
  if (procedurePageContext) {
    procedureId = procedurePageContext.procedureMetadata.id;
    const {selectedMethodCid, idToCidMap} = procedurePageContext;
    // procedureMethodDefinitionVersionId can still be undefined if the method is not yet persisted
    procedureMethodDefinitionVersionId = Object.entries(idToCidMap).find(
      ([_, cid]) => cid === selectedMethodCid
    )?.[0];
  }
  return useMemo(
    () => ({
      entryId: fileId && APIUtil.isEntry(fileId) ? fileId : undefined,
      procedureStepExecutionId:
        deprecatedStepCreationContextId &&
        APIUtil.isProcedureStepExecutionInstance(deprecatedStepCreationContextId)
          ? deprecatedStepCreationContextId
          : undefined,
      procedureId,
      procedureMethodDefinitionVersionId,
      requestV2SubmissionId:
        documentParentId != null && APIUtil.isRequestV2Submission(documentParentId)
          ? documentParentId
          : undefined,
    }),
    [
      fileId,
      procedureId,
      procedureMethodDefinitionVersionId,
      deprecatedStepCreationContextId,
      documentParentId,
    ]
  );
}

export function isActionFunctionContextEnabled(): boolean {
  return (
    BenchlingConfig.SAVE_ENTRY_CREATION_CONTEXT ||
    BenchlingConfig.PROCEDURES_ENABLE ||
    BenchlingConfig.STUDIES_ENABLE ||
    BenchlingConfig.REQUESTS_V2_ENABLE
  );
}
